/**
 * Demo Services — mock/static simulation behavior
 *
 * Used when appMode === 'demo'. Returns fixture data, never calls real APIs.
 * Matches the same interface as realServices.
 */

const _delay = (ms = 300) => new Promise(r => setTimeout(r, ms));

// ─── Dictation (blocked in demo: no mic, no Whisper) ────────────
export const DictationService = {
  getStatus: async () => ({ dictation_enabled: false, _demo: true }),

  createSession: async (data) => {
    await _delay(200);
    return {
      success: true,
      session_id: `DEMO-DIC-${Date.now()}`,
      status: 'recording',
      _demo: true,
    };
  },

  updateSession: async (sessionId, action) => {
    await _delay(100);
    const status = { pause: 'paused', resume: 'recording', stop: 'stopped' }[action] || 'stopped';
    return { success: true, session_id: sessionId, status, _demo: true };
  },

  uploadChunk: async () => {
    await _delay(100);
    return { success: true, chunk_id: 'demo-chunk', chunk_index: 0, size_bytes: 0, _demo: true };
  },

  finalizeSession: async (sessionId) => {
    await _delay(500);
    return {
      success: true,
      session_id: sessionId,
      transcript: '',
      chunk_count: 0,
      method: 'demo',
      _demo: true,
    };
  },

  getSession: async () => ({ session_id: 'demo', status: 'finalized', _demo: true }),
  listSessions: async () => ({ sessions: [], count: 0, _demo: true }),
};

// ─── FHIR (read allowed in demo, writes blocked) ────────────────
// Method names match api.js FhirService so callers can swap transparently.
export const FhirService = {
  // Reads — return empty/default fixtures
  health: async () => ({ fhir_enabled: false, _demo: true }),
  getPatientSummary: async (patientId) => ({ patient_id: patientId, sections: {}, _demo: true }),
  getSyncStatus: async (patientId) => ({ patient_id: patientId, synced: false, _demo: true }),
  syncPatient: async () => ({ success: false, message: 'Sync disabled in demo mode', _demo: true }),
  getWriteConfig: async () => ({ can_write: false, _demo: true }),

  // Writes — always blocked
  writeProblem: async () => ({ success: false, blocked: true, message: 'Write blocked in demo mode', _demo: true }),
  writeMedication: async () => ({ success: false, blocked: true, message: 'Write blocked in demo mode', _demo: true }),
  writeAllergy: async () => ({ success: false, blocked: true, message: 'Write blocked in demo mode', _demo: true }),
  writeNote: async () => ({ success: false, blocked: true, message: 'Write blocked in demo mode', _demo: true }),
};

// ─── FHIR Subscriptions (blocked in demo) ────────────────────────
export const FhirSubscriptionService = {
  getStatus: async () => ({ subscriptions_enabled: false, _demo: true }),
  getEvents: async () => ({ events: [], count: 0, _demo: true }),
  getQueue: async () => ({ jobs: [], count: 0, _demo: true }),
};

// ─── Lens (return demo fixture, no real processing) ──────────────
// Method names match lensService.js exports so callers can swap transparently.
const _demoJob = (jobId) => ({
  job_id: jobId || `DEMO-LENS-${Date.now()}`,
  status: 'completed',
  progress: 100,
  extracted_fields: { document_type: 'Demo Document', summary: 'Demo extraction', sections: [] },
  errors: [],
  _demo: true,
});

export const LensService = {
  analyze: async () => {
    await _delay(800);
    return { success: true, analysis_id: `DEMO-LENS-${Date.now()}`, status: 'demo', _demo: true };
  },
  extract: async () => { await _delay(400); return _demoJob(); },
  extractAndWait: async (file, opts) => {
    await _delay(800);
    const job = _demoJob();
    opts?.onProgress?.(job);
    return job;
  },
  pollJob: async (jobId) => { await _delay(200); return _demoJob(jobId); },
  listJobs: async () => ({ jobs: [], total: 0, _demo: true }),
  deleteJob: async () => ({ success: true, _demo: true }),
  retryJob: async (jobId) => { await _delay(400); return _demoJob(jobId); },
};

/**
 * BLOCKED_ENDPOINTS — patterns that must never be called in demo mode.
 * Used by enforcement tests and the demo service guard.
 */
export const BLOCKED_ENDPOINT_PATTERNS = [
  '/api/dictation/',
  '/api/fhir/patient/',            // write-capable FHIR paths
  '/api/fhir/subscriptions/webhook',
];

/**
 * BLOCKED_CAPABILITIES — browser APIs that must not be invoked in demo mode.
 */
export const BLOCKED_CAPABILITIES = [
  'navigator.mediaDevices.getUserMedia',
  'MediaRecorder',
];
